import { PATH_SEPARATOR, type Flag } from "./studioTypes";

/** A finding title shortened for the row. The detector writes every node with its full
 *  ancestry ("Fabric care › Downy › Beads › Odor Blasters splits the Fabric care › Downy ›
 *  Beads › Original block"); the row shows the nodes ("Odor Blasters splits the Original
 *  block") and the ancestry once, as `context`. The ancestry is the flag's own scope
 *  path minus its last node, so nothing is guessed from the wording. */
export interface ShortFlagTitle {
  title: string;
  /** The ancestry removed from the title; null when there was none to remove. */
  context: string | null;
}

export const shortFlagTitle = (title: string, scopePath: readonly string[]): ShortFlagTitle => {
  // Longest ancestry that the title actually spells out, so a title written one level above
  // the flag's scope still shortens — and that every node the title names shares, so the
  // context above the title is never spelled out again inside it.
  const root = scopePath[0] + PATH_SEPARATOR;
  const mentions = title.split(root).length - 1;
  for (let depth = scopePath.length - 1; depth >= 1; depth -= 1) {
    const prefix = scopePath.slice(0, depth).join(PATH_SEPARATOR) + PATH_SEPARATOR;
    if (!title.includes(prefix) || title.split(prefix).length - 1 < mentions) continue;
    return {
      title: title.split(prefix).join("").replace(/\s+/g, " ").trim(),
      context: scopePath.slice(0, depth).join(PATH_SEPARATOR),
    };
  }
  return { title, context: null };
};

/** A title names at most this many of the blocks it lists; the rest are counted. */
export const BLOCKS_NAMED = 3;

const INTRUDER_NOTE = "intruder: ";

/** The blocks a card lists as splitting its block (the detector notes each one), as the
 *  short title spells them: without the `context` above them. */
export const listedBlocksOf = (flag: Pick<Flag, "evidence">, context: string | null): string[] =>
  (flag.evidence.notes ?? [])
    .filter((note) => note.startsWith(INTRUDER_NOTE))
    .map((note) => {
      const name = note.slice(INTRUDER_NOTE.length);
      return context ? name.split(context + PATH_SEPARATOR).join("") : name;
    });

/** A title that lists more than `BLOCKS_NAMED` blocks names the first two and counts the rest:
 *  "Deep Clean, Charcoal + Clay and 7 more split the Eucalyptus + Birch block". */
export const withBlocksCounted = (
  title: string,
  listed: readonly string[],
  and = " and ",
  more = (names: string, count: number) => `${names} and ${count} more`,
): string => {
  const spelled = listed.join(and);
  if (listed.length <= BLOCKS_NAMED || !title.startsWith(spelled)) return title;
  const named = BLOCKS_NAMED - 1;
  return `${more(listed.slice(0, named).join(", "), listed.length - named)}${title.slice(spelled.length)}`;
};

/** The block a card's fix mends, as a note names it in a few words: the brand and the block's
 *  own name ("Old Spice › Mainstream deo"). A card that lists more blocks than a title names is
 *  named by the block they stand in, with their count (`gathered`). */
export const fixedBlockOf = (
  flag: Pick<Flag, "title" | "scopePath" | "evidence">,
): { block: string; gathered: number | null } => {
  const { context } = shortFlagTitle(flag.title, flag.scopePath);
  const listed = listedBlocksOf(flag, context);
  const gathered = listed.length > BLOCKS_NAMED && context !== null;
  // A card's scope can end in words that are no block ("shelf 4").
  const path = gathered
    ? context.split(PATH_SEPARATOR)
    : flag.scopePath.filter((node) => !/^shelf \d/.test(node));
  return {
    block: [...new Set([path[0], path[path.length - 1]])].join(PATH_SEPARATOR),
    gathered: gathered ? listed.length : null,
  };
};
